import { StyleSheet } from '@react-pdf/renderer';
import { pdfStyles } from '../styles/pdf-styles';

/**
 * Risk level type for PDF and print components
 */
export type RiskLevel = 'low' | 'moderate' | 'high' | 'imminent';

/**
 * Normalize risk string to lowercase risk level
 */
export function normalizeRiskLevel(risk: string | undefined): string {
  if (!risk) return 'faible';
  const normalized = risk.toLowerCase();
  if (normalized.includes('faible')) return 'faible';
  if (normalized.includes('modér') || normalized.includes('modere')) return 'modere';
  if (normalized.includes('élev') || normalized.includes('eleve') || normalized.toLowerCase()?.includes('élev') || normalized.toLowerCase()?.includes('elev')) return 'eleve';
  if (normalized.includes('imminent')) return 'imminent';
  return 'faible';
}

/**
 * Map French risk values to English RiskLevel enum
 * Handles abbreviations: 'f' -> 'faible', 'm' -> 'modere', 'e' -> 'eleve', 'i' -> 'imminent'
 */
export function mapRiskToEnglish(risk: string): RiskLevel | null {
  if (!risk) return null;
  let normalized = risk?.toLowerCase() || 'faible';

  // Handle abbreviations
  if (risk === 'f') {
    normalized = 'faible';
  }
  if (risk === 'm') {
    normalized = 'modere';
  }
  if (risk === 'e') {
    normalized = 'eleve';
  }
  if (risk === 'i') {
    normalized = 'imminent';
  }

  if (normalized === 'faible' || normalized.includes('faible')) return 'low';
  if (normalized === 'modere' || normalized === 'modéré' || normalized.includes('modéré') || normalized.includes('modere')) return 'moderate';
  if (normalized === 'eleve' || normalized === 'élevé' || normalized.includes('élevé') || normalized.includes('eleve') || normalized?.toLowerCase()?.includes('elev')) return 'high';
  if (normalized === 'imminent' || normalized.includes('imminent')) return 'imminent';
  return 'low';
}

/**
 * Format risk label for display (French)
 */
export function formatRiskLabel(risk: string): string {
  if (!risk) return '';
  let normalized = risk?.toLowerCase() || 'faible';

  // Handle abbreviations
  if (risk === 'f') {
    normalized = 'faible';
  }
  if (risk === 'm') {
    normalized = 'modere';
  }
  if (risk === 'e') {
    normalized = 'eleve';
  }
  if (risk === 'i') {
    normalized = 'imminent';
  }

  if (normalized === 'faible' || normalized.includes('faible')) return 'Faible';
  if (normalized === 'modere' || normalized === 'modéré' || normalized.includes('modéré') || normalized.includes('modere')) return 'Modéré';
  if (normalized === 'eleve' || normalized === 'élevé' || normalized.includes('élevé') || normalized.includes('eleve')) return 'Élevé';
  if (normalized === 'imminent' || normalized.includes('imminent')) return 'Imminent';
  return risk || 'Faible';
}

/**
 * Get risk color style for PDF
 */
export function getRiskColor(risk: string): typeof pdfStyles.riskLow {
  const normalized = risk?.toLowerCase() || '';
  if (normalized.includes('faible')) return pdfStyles.riskLow;
  if (normalized.includes('modér') || normalized.includes('modere')) return pdfStyles.riskModerate;
  if (normalized.includes('élev') || normalized.includes('eleve')) return pdfStyles.riskHigh;
  if (normalized.includes('imminent')) return pdfStyles.riskImminent;
  return pdfStyles.riskLow;
}

/**
 * Get risk style based on RiskLevel
 */
export function getRiskStyleByLevel(level: RiskLevel): typeof pdfStyles.riskLow {
  switch (level) {
    case 'low':
      return pdfStyles.riskLow;
    case 'moderate':
      return pdfStyles.riskModerate;
    case 'high':
      return pdfStyles.riskHigh;
    case 'imminent':
      return pdfStyles.riskImminent;
    default:
      return pdfStyles.riskLow;
  }
}
